import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { Masked, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import BlueprintOverlay from "@/components/BlueprintOverlay";
import ArtworkImage from "@/components/ArtworkImage";
import Seo from "@/components/Seo";
import { eur, artText } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const CHAPTERS = [
  { n: "01", title: "home.ch1t", text: "home.ch1x", meta: "home.ch1m" },
  { n: "02", title: "home.ch2t", text: "home.ch2x", meta: "home.ch2m" },
  { n: "03", title: "home.ch3t", text: "home.ch3x", meta: "home.ch3m" },
];

export default function Home() {
  const { artworks, loading, settings } = useStore();
  const { t, lang } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yMeta = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const atelierPhoto = settings.atelier_photo_url || "";

  const featured = artworks.filter((a) => a.featured && a.availability === "In Atelier").slice(0, 3);
  const hero = artworks.find((a) => a.artwork_id === "art-chassis9") || artworks[0];

  return (
    <div data-testid="home-page">
      <Seo description={t("seo.home")} image={atelierPhoto || hero?.image_url} />
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden">
        <div className="bp-grid pointer-events-none absolute inset-0" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink to-transparent" />
        <div className="relative mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-20 pt-32 sm:px-10 lg:grid-cols-12 lg:px-16">
          <div className="lg:col-span-7">
            <Masked delay={0.15}>
              <span className="eyebrow">{t("brand.eyebrow")}</span>
            </Masked>
            <h1 className="mt-6">
              <Masked delay={0.3}>
                <span className="font-cursive text-[19vw] leading-[0.95] text-bone sm:text-8xl lg:text-[7.5rem]">
                  DeMoivre
                </span>
              </Masked>
            </h1>
            <Masked delay={0.55}>
              <span className="mt-6 block max-w-xl font-serif text-2xl font-light italic leading-snug text-bone-dim sm:text-3xl">
                {t("brand.tagline")}
              </span>
            </Masked>
            <Masked delay={0.75}>
              <span className="mt-6 block max-w-lg text-sm leading-relaxed text-bone-dim/70 sm:text-base">
                {t("home.intro")}
              </span>
            </Masked>
            <Masked delay={0.9}>
              <span className="mt-10 flex flex-wrap gap-4">
                <Link
                  data-testid="hero-shop-cta"
                  to="/shop"
                  className="group flex items-center gap-3 border border-brass/60 bg-brass/10 px-8 py-4 font-mono text-[11px] tracking-[0.35em] text-brass transition-all duration-300 hover:bg-brass hover:text-ink"
                >
                  {t("home.ctaCollection")}
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  data-testid="hero-commission-cta"
                  to="/commissions"
                  className="flex items-center gap-3 border border-bone/20 px-8 py-4 font-mono text-[11px] tracking-[0.35em] text-bone transition-colors duration-300 hover:border-brass/50 hover:text-brass"
                >
                  {t("home.ctaCommission")}
                </Link>
              </span>
            </Masked>
          </div>

          <div className="relative lg:col-span-5">
            {hero ? (
              <motion.div style={{ y: yImg }} className="group relative">
                <ArtworkImage art={hero} annotate={false} />
                <motion.div style={{ y: yMeta }} className="mt-4 flex items-center justify-between font-mono text-[9px] tracking-[0.25em] text-bone-muted">
                  <span>{hero.dwg_no || "DEMOIVRE"}</span>
                  <span>{hero.dimensions}</span>
                  <span className="text-brass">ÉCHELLE 1:1</span>
                </motion.div>
              </motion.div>
            ) : (
              <motion.div style={{ y: yImg }} className="relative">
                <div className="relative aspect-[4/5] overflow-hidden border border-bone/10 bg-ink-2">
                  <span className="tick" style={{ top: 10, left: 10 }} />
                  <span className="tick" style={{ top: 10, right: 10 }} />
                  <span className="tick" style={{ bottom: 10, left: 10 }} />
                  <span className="tick" style={{ bottom: 10, right: 10 }} />
                  <img
                    src="https://images.unsplash.com/photo-1712696779652-dfca8766c5f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxtZWNoYW5pY2FsJTIwZW5naW5lZXJpbmclMjBibHVlcHJpbnQlMjB0ZWNobmljYWwlMjBkcmF3aW5nJTIwYXJ0d29yayUyMGNhbnZhc3xlbnwwfHx8fDE3ODg4MDY1ODV8MA&ixlib=rb-4.1.0&q=85"
                    alt="Dessin technique — DeMoivre"
                    className="h-full w-full object-cover"
                  />
                </div>
                <motion.div style={{ y: yMeta }} className="mt-4 flex items-center justify-between font-mono text-[9px] tracking-[0.25em] text-bone-muted">
                  <span>DEMOIVRE — ATELIER</span>
                  <span>PARIS</span>
                  <span className="text-brass">ÉCHELLE 1:1</span>
                </motion.div>
              </motion.div>
            )}
            <div className="pointer-events-none absolute -left-10 -top-10 h-24 w-24 border-l border-t border-brass/30" />
            <div className="pointer-events-none absolute -bottom-10 -right-10 h-24 w-24 border-b border-r border-brass/30" />
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.4em] text-bone-muted/60">
          {t("home.scroll")}
        </div>
      </section>

      <Marquee />

      {/* ---------- MANIFESTO ---------- */}
      <section className="relative mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:px-16 lg:py-36">
        <Reveal>
          <p className="eyebrow">{t("home.manifestoEyebrow")}</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-light leading-tight text-bone sm:text-4xl lg:text-5xl">
            {t("home.manifestoTitleA")}<br />
            <span className="italic text-brass">{t("home.manifestoTitleB")}</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-px border border-bone/10 bg-bone/10 md:grid-cols-3">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.12} className="bg-ink">
              <div className="group flex h-full flex-col p-8 transition-colors duration-500 hover:bg-ink-3 lg:p-10">
                <span className="font-mono text-[10px] tracking-[0.35em] text-brass">{t("home.chapterLabel")} {c.n}</span>
                <h3 className="mt-5 font-serif text-2xl text-bone sm:text-3xl">{t(c.title)}</h3>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-bone-dim/80">{t(c.text)}</p>
                <span className="mt-8 border-t border-bone/10 pt-4 font-mono text-[9px] tracking-[0.25em] text-bone-muted">
                  {t(c.meta)}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- FEATURED ---------- */}
      <section className="relative border-t border-bone/10 bg-ink-2">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 lg:px-16 lg:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">{t("home.collectionEyebrow")}</p>
              <h2 className="mt-4 font-serif text-3xl font-light text-bone sm:text-4xl lg:text-5xl">
                {t("home.collectionTitle")}
              </h2>
            </div>
            <Link
              data-testid="featured-view-all-link"
              to="/shop"
              className="group flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-brass"
            >
              {t("home.collectionAll")}
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          {loading ? (
            <p className="mt-16 font-mono text-[11px] tracking-[0.3em] text-bone-muted">{t("home.loading")}</p>
          ) : featured.length === 0 ? (
            <p className="mt-16 border border-bone/10 px-6 py-14 text-center font-mono text-[10px] tracking-[0.3em] text-bone-muted">
              {t("home.noPieces")}
            </p>
          ) : (
            <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((art, i) => (
                <Reveal key={art.artwork_id} delay={i * 0.12}>
                  <Link to={`/shop/${art.artwork_id}`} className="group block" data-testid={`featured-card-${art.artwork_id}`}>
                    <ArtworkImage art={art} />
                    <div className="mt-4 flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-xl text-bone transition-colors group-hover:text-brass">
                          {artText(art, "title", lang)}
                        </h3>
                        <p className="mt-1 font-mono text-[9px] tracking-[0.25em] text-bone-muted">
                          {art.dwg_no} · {art.dimensions}
                        </p>
                      </div>
                      <span className="font-mono text-sm text-brass">
                        {art.price > 0 ? eur(art.price) : t("product.priceOnRequest")}
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ---------- ATELIER TEASER ---------- */}
      <section className="relative overflow-hidden">
        <BlueprintOverlay dimLabel="406 × 508 MM" stamp="DWG — ATELIER" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 sm:px-10 lg:grid-cols-2 lg:px-16 lg:py-36">
          <Reveal>
            <div className="relative aspect-[4/3] overflow-hidden border border-bone/10 bg-ink-2">
              <span className="tick" style={{ top: 10, left: 10 }} />
              <span className="tick" style={{ top: 10, right: 10 }} />
              <span className="tick" style={{ bottom: 10, left: 10 }} />
              <span className="tick" style={{ bottom: 10, right: 10 }} />
              {atelierPhoto ? (
                <img
                  data-testid="home-atelier-photo"
                  src={atelierPhoto}
                  alt="L'atelier DeMoivre"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div data-testid="home-atelier-photo-placeholder" className="flex h-full w-full flex-col items-center justify-center gap-3 bp-grid-fine">
                  <span className="tick" style={{ position: "relative" }} />
                  <span className="font-mono text-[10px] tracking-[0.3em] text-bone-muted">DEMOIVRE — ATELIER</span>
                </div>
              )}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="eyebrow">{t("home.atelierEyebrow")}</p>
            <h2 className="mt-4 font-serif text-3xl font-light leading-tight text-bone sm:text-4xl">
              {t("home.atelierTitleA")}<br />{t("home.atelierTitleB")}
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-bone-dim/80 sm:text-base">
              {t("home.atelierText")}
            </p>
            <Link
              data-testid="home-process-link"
              to="/process"
              className="group mt-8 inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-brass"
            >
              {t("home.atelierCta")}
              <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="relative border-t border-bone/10">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center sm:px-10 lg:py-36">
          <Reveal>
            <p className="eyebrow">{t("home.ctaEyebrow")}</p>
            <p className="mx-auto mt-8 font-cursive text-6xl text-bone sm:text-7xl">{t("home.ctaScript")}</p>
            <p className="mt-2 font-serif text-2xl font-light italic text-bone-dim sm:text-3xl">
              {t("home.ctaSerif")}
            </p>
            <p className="mx-auto mt-6 max-w-lg text-sm leading-relaxed text-bone-dim/70">
              {t("home.ctaText")}
            </p>
            <Link
              data-testid="home-commission-cta"
              to="/commissions"
              className="mt-10 inline-flex items-center gap-3 border border-brass/60 bg-brass/10 px-10 py-4 font-mono text-[11px] tracking-[0.35em] text-brass transition-all duration-300 hover:bg-brass hover:text-ink"
            >
              {t("home.ctaButton")} <ArrowRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
